/**
 * A refusal, kept where the request dialog can find it.
 *
 * The main app does this the same way: a page the person may not open, or
 * an action the backend refused, becomes one record here; a dialog over the
 * page reads it and turns it into a request to whoever grants portal access.
 * No React, no HTTP - so the axios interceptor can write to it and a unit
 * test can read it.
 */

export interface AccessDenial {
  /** Everything needed that the rep lacks; the first is what is asked for. */
  readonly permissions: readonly string[];
  /** The path they were on, sent with the ask so an admin knows. */
  readonly page: string;
  /**
   * A page they may not open, a button the backend refused, or a portal
   * with nothing switched on at all - the first-time ask.
   */
  readonly kind: 'page' | 'action' | 'setup';
  /** The backend's own sentence for an action refusal. */
  readonly message?: string;
}

type Listener = () => void;

let denial: AccessDenial | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  [...listeners].forEach((listener) => listener());
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDenial(): AccessDenial | null {
  return denial;
}

/** Records a refusal. The same refusal twice in a row is recorded once. */
export function denyAccess(next: AccessDenial): void {
  if (
    denial &&
    denial.kind === next.kind &&
    denial.page === next.page &&
    denial.permissions.join(',') === next.permissions.join(',')
  ) {
    return;
  }
  denial = next;
  emit();
}

export function clearDenial(): void {
  if (denial === null) return;
  denial = null;
  emit();
}

/** The code the backend sends with a refused portal action. */
export const PORTAL_REFUSAL_CODE = 'PORTAL_PERMISSION_REQUIRED';

/**
 * The tags a refused call named, or null when the error is something else.
 * Reads the axios error shape without importing axios, so this file stays
 * pure.
 */
export function refusalFromError(
  error: unknown
): { permissions: string[]; message: string } | null {
  const failed = error as {
    config?: { method?: string };
    response?: { status?: number; data?: unknown };
  } | null;
  // Only a refused WRITE is something the rep did and can ask about. A
  // refused read is a page's background fetch - the inventory page asks
  // for orders it may not see - and the page itself is still theirs to
  // open; a dialog over it would be asking for a thing they did not touch.
  const method = (failed?.config?.method ?? 'get').toLowerCase();
  if (method === 'get' || method === 'head' || method === 'options') {
    return null;
  }
  const response = failed?.response;
  if (!response || response.status !== 403) return null;
  const body = response.data as
    | { code?: unknown; permissions?: unknown; error?: unknown }
    | null
    | undefined;
  if (!body || body.code !== PORTAL_REFUSAL_CODE) return null;
  const permissions = Array.isArray(body.permissions)
    ? body.permissions.filter((p): p is string => typeof p === 'string')
    : [];
  if (permissions.length === 0) return null;
  return {
    permissions,
    message: typeof body.error === 'string' ? body.error : ''
  };
}

export interface RequestOutcome {
  /** How many people were told. When escalated, these are platform staff. */
  readonly admins: number;
  /** The company has no admin of its own, so Global Link Plus took it. */
  readonly escalated: boolean;
  /** They had already asked for this and it is still open. */
  readonly alreadyPending?: boolean;
}

/** The whole line once a request has gone: one sentence the rep can act on. */
export function sentMessage(outcome: RequestOutcome): string {
  if (outcome.alreadyPending) {
    return 'You have already asked for this. It is still waiting to be answered.';
  }
  if (outcome.admins < 1) {
    return 'Your request is on file, but nobody could be reached just now. Please contact Global Link Plus support.';
  }
  if (outcome.escalated) {
    return 'Your request has been sent to the Global Link Plus team.';
  }
  return 'Your request has been sent to your company admin.';
}
